<script setup lang="ts">
import type { Menu } from 'primevue';
import type { MenuItem, MenuItemCommandEvent } from 'primevue/menuitem';
import { computed, ref } from 'vue';

import VButton from '@components/buttons/VButton/VButton.vue';
import VConfirmOpenLink from '@components/confirm-open-link/VConfirmOpenLink/VConfirmOpenLink.vue';
import { useI18n } from '@modules/i18n/composables';

import type { IContactsMenuButtonProps, IContactsMenuOption } from './types';
import { IContactsTypes } from './types';

const props = defineProps<IContactsMenuButtonProps>();

const showConfirmDialog = ref(false);
const selectedLink = ref<string>();

const { getTranslation } = useI18n();

const menu = ref<InstanceType<typeof Menu>>();
const options = computed<IContactsMenuOption[]>(() => [
  {
    label: getTranslation(props.translateKey, 'CONTACTS_EMAIL').value,
    icon: 'pi pi-envelope',
    type: IContactsTypes.EMAIL,
    value: props.contacts?.email,
  },
  {
    label: getTranslation(props.translateKey, 'CONTACTS_TELEGRAM').value,
    icon: 'pi pi-telegram',
    type: IContactsTypes.TELEGRAM,
    value: props.contacts?.telegram,
  },
  {
    label: getTranslation(props.translateKey, 'CONTACTS_WHATSAPP').value,
    icon: 'pi pi-whatsapp',
    type: IContactsTypes.WHATSAPP,
    value: props.contacts?.whatsapp,
  },
  {
    label: getTranslation(props.translateKey, 'CONTACTS_MS_TEAMS').value,
    type: IContactsTypes.MS_TEAMS,
    value: props.contacts?.ms_teams,
  },
]);

const contactsMenuitems = computed<MenuItem[]>(() => options.value.filter((item) => item.value).map(({ label, value, icon, type }) => ({
  label,
  value,
  icon,
  key: type,
  command: confirmVisitLink,
})),
);

function confirmVisitLink(event: MenuItemCommandEvent): void {
  selectedLink.value = event.item.value;
  showConfirmDialog.value = true;
}

function toggle(event: Event): void {
  menu.value?.toggle(event);
}
</script>

<template>
  <VButton v-data-qa-auto-id="'VContactsMenuButton'"
    :label="buttonTitle"
    :disabled="disabled"
    icon="pi-envelope"
    outlined
    @click="toggle"
  />
  <PMenu
    ref="menu"
    :model="contactsMenuitems"
    popup
  >
    <template #itemicon="{ item }">
      <svg
        v-if="item.key === IContactsTypes.MS_TEAMS"
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M9.186 4.797a2.42 2.42 0 1 0-2.86-2.448h1.178c.929 0 1.682.753 1.682 1.682zm-4.295 7.738h2.613c.929 0 1.682-.753 1.682-1.682V5.58h2.783a.7.7 0 0 1 .682.716v4.294a4.197 4.197 0 0 1-4.093 4.293c-1.618-.04-3-.99-3.667-2.35Zm10.737-9.372a1.674 1.674 0 1 1-3.349 0 1.674 1.674 0 0 1 3.349 0m-2.238 9.488-.12-.002a5.2 5.2 0 0 0 .381-2.07V6.306a1.7 1.7 0 0 0-.15-.725h1.792c.39 0 .707.317.707.707v3.765a2.6 2.6 0 0 1-2.598 2.598z" />
        <path d="M.682 3.349h6.822c.377 0 .682.305.682.682v6.822a.68.68 0 0 1-.682.682H.682A.68.68 0 0 1 0 10.853V4.03c0-.377.305-.682.682-.682Zm5.206 2.596v-.72h-3.59v.72h1.357V9.66h.87V5.945z" />
      </svg>
      <i
        v-else
        :class="item.icon"
      />
    </template>
  </PMenu>

  <VConfirmOpenLink
    v-model:visible="showConfirmDialog"
    :link="selectedLink"
  />
</template>
